import React, { useState, useEffect } from 'react';
import T from 'prop-types';
import { pick } from 'lodash-es';

import { getSimilarApps, getReportCharts } from '../api';
import { dispatchSwitchAppFlowEvent } from '../../shared/custom-events';

const stepsCount = 3;
const stepDelay = 2000;

const mockDelay = (delay) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(true), delay);
  });

const processingText = {
  1: 'We’re building your competitive market',
  2: 'Analyzing market share and user data',
  3: 'Determining growth statistics',
};

const ProcessingSmartSearch = ({ selectedApps, onAppsChange, onChartsChange, onStepChange }) => {
  const [processingStep, setProcessingStep] = useState(1);

  // Changing processing step
  useEffect(() => {
    let timeoutID;

    if (processingStep < stepsCount) {
      timeoutID = setTimeout(() => {
        setProcessingStep((prevState) => prevState + 1);
      }, stepDelay);
    }

    return () => clearTimeout(timeoutID);
  }, [processingStep]);

  useEffect(() => {
    if (selectedApps.length === 1) {
      const [selectedApp] = selectedApps;

      // Using Promise all to ensure ending animation
      Promise.all([
        getSimilarApps(pick(selectedApp, ['ref_no', 'store'])),
        mockDelay(2 * stepDelay),
      ])
        .then(([{ data }]) => {
          const similarApps = [selectedApp, ...data.results.slice(0, 4)];
          const requestData = { apps: similarApps.map(({ ref_no, store }) => ({ ref_no, store })) };

          onAppsChange(similarApps);
          return Promise.all([getReportCharts(requestData), mockDelay(1 * stepDelay)]);
        })
        .then(([{ data }]) => {
          onChartsChange(data);
          onStepChange('report-generation');
        })
        .catch(() => {
          dispatchSwitchAppFlowEvent({ appFlow: 'multi', selectedApp });
          onAppsChange([]);
          onChartsChange();
          onStepChange('app-selection');
        });
    } else if (selectedApps.length > 1) {
      const requestData = { apps: selectedApps.map(({ ref_no, store }) => ({ ref_no, store })) };

      Promise.all([getReportCharts(requestData), mockDelay(3 * stepDelay)])
        .then(([{ data }]) => {
          onChartsChange(data);
          onStepChange('report-generation');
        })
        .catch(() => {
          onStepChange('error-step');
        });
    }
  }, []);

  return (
    <div className="processing-smart-search">
      <h3 className="processing-smart-search__pretitle">Hold tight!</h3>
      <h2 className="processing-smart-search__title">{processingText[processingStep]}</h2>
      <div className="processing-smart-search__loading">
        <span className="loading-spinner animated-rotation" />
      </div>
    </div>
  );
};

ProcessingSmartSearch.propTypes = {
  selectedApps: T.arrayOf(
    T.shape({
      ref_no: T.oneOfType([T.string, T.number]),
      store: T.string,
    })
  ).isRequired,
  onAppsChange: T.func.isRequired,
  onChartsChange: T.func.isRequired,
  onStepChange: T.func.isRequired,
};

export default ProcessingSmartSearch;
